import {JSX, lazy, LazyExoticComponent, ReactNode, Suspense, useEffect, useMemo} from "react";

export interface InitializerMethods {
	isReady: () => boolean;
	initialize: () => Promise<void>;
	reset: () => void;
}

export interface InitializerProps {
	initializer: InitializerMethods;
	children: ReactNode;
	loading?: ReactNode;
	onInitialized?: () => void;
}

type InnerProps = { children?: ReactNode; onInitialized?: () => void };
type InnerComponent = (props: InnerProps) => JSX.Element | null;

// Cache one lazy component per initializer instance. `lazy` runs its factory exactly once and
// memoizes the result, so keying the cache by the initializer guarantees `initialize()` fires only
// once per initializer — even when React StrictMode mounts, unmounts and remounts the component in
// development (which discards in-component state such as useMemo). A different initializer prop
// produces a new cache entry and re-runs initialization. This is what the old module-level
// `currentInitializer` + module-level `lazy` pairing protected against, but without the bugs of a
// single shared global (which could never re-initialize and broke with multiple instances).
const initializerComponents = new WeakMap<InitializerMethods, LazyExoticComponent<InnerComponent>>();

function getInitializerComponent(initializer: InitializerMethods): LazyExoticComponent<InnerComponent> {
	let component = initializerComponents.get(initializer);
	if (!component) {
		component = lazy(async () => {
			try {
				await initializer.initialize();

				return {
					default: ({children, onInitialized}: InnerProps) => {
						useEffect(() => onInitialized?.(), [onInitialized]);

						return initializer.isReady() ? <>{children}</> : null;
					}
				};
			} catch (error) {
				// React Suspense doesn't automatically propagate async errors,
				// so we must rethrow inside a component that will be rendered.
				return {
					default: () => {
						throw error;
					}
				};
			}
		});
		initializerComponents.set(initializer, component);
	}

	return component;
}

export default function AsyncInitializer({
	                                         initializer,
	                                         children,
	                                         loading,
	                                         onInitialized
                                         }: InitializerProps): JSX.Element {
	const AsyncInitializerInner = useMemo(() => getInitializerComponent(initializer), [initializer]);

	return <Suspense fallback={loading}>
		<AsyncInitializerInner onInitialized={onInitialized}>{children}</AsyncInitializerInner>
	</Suspense>;
}
